/**
 * Frontal block skill for Shield enemies — instant activation, reduces incoming damage within a frontal cone.
 * No windup — blocks are reactive. Phase sequence: active → cooldown.
 * NOTE: canActivateExtra() calls stamina.consume(), and the base activate() also calls
 * canActivateExtra() — this double-consume is a pre-existing behavior that's preserved.
 */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';
import type { EnemyStamina } from './EnemyStamina';
import { degreesToRadians } from '../../utils/Math';

export interface EnemyBlockConfig {
  blockDurationMs: number;
  blockAngle: number;
  damageReduction: number;
  cooldownMs: number;
  staminaCost: number;
}

/** Reactive block that consumes stamina and reduces incoming damage during the active phase. */
export class EnemyBlockSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['active', 'cooldown'] as const;
  private readonly config: EnemyBlockConfig;
  private readonly _stamina: EnemyStamina;

  /** Enemy's facing direction — set externally before each activation for angle check. */
  private _facingX: number = 0;
  private _facingY: number = 1;

  constructor(skillId: string, config: EnemyBlockConfig, stamina: EnemyStamina) {
    super();
    this.skillId = skillId;
    this.config = config;
    this._stamina = stamina;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'active': return this.config.blockDurationMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  /** Stamina check + consume. Called in both canActivate() and activate() — double-consume preserved. */
  protected canActivateExtra(): boolean {
    return this._stamina.consume(this.config.staminaCost);
  }

  /** Block is only active during the active phase (not recovery). */
  isActive(): boolean { return this.phase === 'active'; }

  /** Set the enemy's facing direction for block angle calculation. */
  setFacing(facingX: number, facingY: number): void {
    this._facingX = facingX;
    this._facingY = facingY;
  }

  isInDamagePhase(): boolean { return false; }

  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'active') return { vx: 0, vy: 0 };
    return null;
  }

  getDamageInfo(): EnemySkillDamageInfo | null { return null; }

  // --- Block-specific API ---

  /** True when the shield is actively blocking incoming damage. */
  isBlocking(): boolean { return this.phase === 'active'; }

  /**
   * Returns the damage reduction multiplier (0 to 1) for a hit coming from the given direction.
   * Returns 1.0 (no reduction) if the hit is outside the frontal block cone or block is not active.
   * hitDirX/Y is the normalized direction FROM attacker TO this enemy.
   */
  getBlockReduction(hitDirX: number, hitDirY: number): number {
    if (!this.isBlocking()) return 1.0;

    const reverseDot = -(hitDirX * this._facingX + hitDirY * this._facingY);
    const halfAngleRad = degreesToRadians(this.config.blockAngle / 2);
    const threshold = Math.cos(halfAngleRad);

    if (reverseDot >= threshold) {
      return 1.0 - this.config.damageReduction;
    }
    return 1.0;
  }
}
